<script setup lang="ts">
import merge from 'lodash/merge';

interface FormImagesMatching
{
    content: {
        value: {
            description: string;
            questions: SingleImageMatch[];
        };
    };
}

interface SingleImageMatch
{
    id: number;
    picture: File;
    description: string;
    answer: string;
}

interface Props
{
    modelValue: any;
}

const props = defineProps<Props>();

const emit = defineEmits<{
    'update:modelValue': [value: any];
}>();

const { $t } = useLocalization();
const { $alert } = useAlerts();

const defaults: any = {
    description: '',
    questions: []
};

const form = computed((): FormType<FormImagesMatching> =>
{
    return props.modelValue;
});

function addImage(): void
{
    const ids = form.value.content.value.questions.map((x: { id: any }) => x.id);

    const maxId = form.value.content.value.questions.length > 0 ? Math.max(...ids) : 1;

    const lastIndex = form.value.content.value.questions.length - 1;

    if (form.value.content.value.questions[lastIndex].answer != '')
    {
        form.value.content.value.questions.push({
            id: maxId + 1,
            picture: null,
            description: '',
            answer: ''
        });
    }
    else
    {
        $alert.warning($t('[[[Nie podano odpowiedzi dla ostatniego elementu.]]]'));
    }
}

function removeImage(index: number): void
{
    const items = form.value.content.value.questions.slice();

    items.splice(index, 1);
    form.value.content.value.questions = items;

    form.value.$errors.clear(`content.value.questions[${index}]`);
}

function triggetModel(): any
{
    return form.value;
}

onBeforeMount((): void =>
{
    form.value.content.value = merge(defaults, form.value.content.value);
    form.value.file = null;

    if (!form.value.content.value.questions.length)
    {
        form.value.content.value.questions = [{
            id: 1,
            picture: null,
            description: '',
            answer: ''
        }];
    }
});

watch(() => form.value.content.value, (value: any): void =>
{
    emit('update:modelValue', triggetModel());
}, { deep: true });

function answerUp(index: number): void
{
    if (index > 0)
    {
        const answers = form.value.content.value.questions.splice(index, 1);

        form.value.content.value.questions.splice(index - 1, 0, answers[0]);

        form.value.$errors.clear();
    }
}

function answerDown(index: number): void
{
    if (index < form.value.content.value.questions.length - 1)
    {
        const answers = form.value.content.value.questions.splice(index, 1);

        form.value.content.value.questions.splice(index + 1, 0, answers[0]);

        form.value.$errors.clear();
    }
}
</script>

<template>
    <IdeoFormGroup :label="$t('[[[Opis]]]')" :invalid-feedback="form.$errors.first('content.value.description')" :state="form.$errors.state('content.value.description')">
        <RichTextEditor
            :id="seconds('matching-' + form.id)"
            v-model="form.content.value.description"
            name="description"
            shortcode
            timeline
        />
    </IdeoFormGroup>
    <IdeoFormGroup :label="$t('[[[Elementy]]]')" :invalid-feedback="form.$errors.first('content.value.questions')" :state="form.$errors.state('content.value.questions')">
        <div class="mb-3 row">
            <div v-for="(question, i) in form.content.value.questions" :key="question.id" class="col-12 col-md-6 mb-3">
                <div class="d-flex bg-light p-2">
                    <div class="d-flex flex-column flex-grow-1">
                        <IdeoFormGroup :label="$t('[[[Obrazek]]]')" required>
                            <IdeoFilePicker v-model="question.picture" media="images" :data-cy="`image-${i}`" />
                        </IdeoFormGroup>
                        <IdeoFormGroup :label="$t('[[[Opis obrazka]]]')">
                            <IdeoFormTextarea v-model="question.description" rows="4" :placeholder="$t('[[[Opis obrazka]]]')" :data-cy="`image-${i}-description`" />
                        </IdeoFormGroup>
                        <IdeoFormGroup :label="$t('[[[Odpowiedź]]]')" required>
                            <IdeoFormTextarea v-model="question.answer" rows="3" :placeholder="$t('[[[Odpowiedź]]]')" :data-cy="`image-${i}-answer`" />
                        </IdeoFormGroup>
                    </div>
                    <div class="input-group-append d-flex align-items-start px-2 py-4 mt-1">
                        <ActionButton
                            variant="danger"
                            icon="far fa-trash"
                            class="me-2"
                            :title="$t('[[[Usuń element]]]')"
                            :confirm="$t('[[[Potwierdzenie usunięcia]]]')"
                            :disabled="form.content.value.questions.length < 2"
                            @click="removeImage(i)"
                        />
                        <ActionButton
                            variant="secondary"
                            icon="far fa-arrow-up"
                            class="ms-auto"
                            :title="$t('[[[Wyżej]]]') "
                            :disabled="i == 0"
                            @click="answerUp(i)"
                        />
                        <ActionButton variant="secondary" icon="far fa-arrow-down" :title="$t('[[[Niżej]]]')" :disabled="i == form.content.value.questions.length - 1" @click="answerDown(i)" />
                    </div>
                </div>
                <small v-if="!form.$errors.state(`content.value.questions[${i}].answer`)" class="form-text text-danger">{{ form.$errors.first(`content.value.questions[${i}].answer`) }}</small>
                <small v-if="!form.$errors.state(`content.value.questions[${i}].picture`)" class="form-text text-danger">{{ form.$errors.first(`content.value.questions[${i}].picture`) }}</small>
            </div>
        </div>
        <div>
            <ActionButton
                variant="outline-dark"
                class="w-100"
                icon="far fa-plus"
                :text="$t('[[[Dodaj odpowiedź]]]')"
                data-cy="addImage"
                @click.prevent="addImage()"
            />
        </div>
    </IdeoFormGroup>
</template>
